import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketingShell, M } from "@/components/MarketingShell";

const TITLE = "About Tiger's Eye — honest feedback on your own writing";
const DESC =
  "Tiger's Eye began as a parent reading their daughter's drafts while away on work. Now it gives students clear, critique-only feedback on essays and dissertations. It never writes or rewrites your work. Built by PRAXIA in the UK.";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://tigerseye.app/about" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://tigerseye.app/about" }],
  }),
  component: AboutPage,
});

const SECTIONS = [
  {
    h: "What Tiger's Eye does",
    p: "You upload a draft, tell us your brief, level of study and referencing style, and get clear, section-by-section feedback on argument, structure, evidence, clarity and referencing — the things markers look for.",
  },
  {
    h: "What it never does",
    p: "Tiger's Eye never writes, rewrites or paraphrases your work. It points out what to look at; you decide what to change. That keeps your words, and your grade, your own — and keeps you on the right side of university AI policies.",
  },
  {
    h: "Why we built it",
    p: "Good feedback before submission is hard to come by. Tutors are busy and study-skills appointments fill up. Tiger's Eye gives every student a careful first reader, at any hour, at a price students can afford.",
  },
  {
    h: "Who it's for",
    p: "School, college and university students in the UK and beyond — from first essays to dissertations. Feedback is tailored to your level of study.",
  },
  {
    h: "Your work stays private",
    p: "Your drafts and reviews are private to your account. They are never shared with other students, sold, or used to advertise to you.",
  },
];

function AboutPage() {
  return (
    <MarketingShell>
      <p className="text-xs uppercase tracking-[0.2em]" style={{ color: M.BRASS }}>About</p>
      <h1 className="mt-3 text-4xl leading-tight" style={{ fontFamily: M.SERIF }}>
        A careful first reader for your own writing.
      </h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed" style={{ color: M.INK_SOFT }}>
        Tiger&apos;s Eye Academic Review is a critique-only feedback tool for students, built by PRAXIA in the UK.
        It began with a parent reading their daughter&apos;s drafts from away on work.
      </p>
      <blockquote
        className="mt-8 max-w-2xl border-l-2 pl-5 text-lg leading-relaxed"
        style={{ fontFamily: M.SERIF, borderColor: M.BRASS, color: M.INK }}
      >
        &ldquo;I built this for my daughter to use when I was away for work and she needed someone to read her
        drafts. It worked so well, we decided to share it. Hopefully you will love and share it too.&rdquo;
      </blockquote>
      <div className="mt-10 flex flex-col gap-8">
        {SECTIONS.map((s) => (
          <section key={s.h}>
            <h2 className="text-2xl" style={{ fontFamily: M.SERIF }}>{s.h}</h2>
            <p className="mt-2 max-w-2xl leading-relaxed" style={{ color: M.INK_SOFT }}>{s.p}</p>
          </section>
        ))}
      </div>
      <p className="mt-10 text-sm" style={{ color: M.INK_SOFT }}>
        Read more:{" "}
        <Link to="/blog/$slug" params={{ slug: "is-ai-feedback-allowed-at-university" }} className="underline" style={{ color: M.INK }}>
          Is AI feedback allowed at UK universities?
        </Link>{" "}
        ·{" "}
        <Link to="/faq" className="underline" style={{ color: M.INK }}>Student FAQ</Link> ·{" "}
        <Link to="/pricing" className="underline" style={{ color: M.INK }}>Pricing</Link>
      </p>
    </MarketingShell>
  );
}
